import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Car, Calendar, DollarSign, Database, Wrench, BarChart, Radio, AlertTriangle, Shield } from 'lucide-react';
import { API } from '../../utils/apiConfig';

export const AdminOverviewTab = ({ stats, revenue, updatingStats, maintenanceRunning, updateAllDriverStats, runMaintenanceTasks, testEndpoints }) => {
  const { t } = useTranslation();

  // Live Guardian mini-feed
  const [guardianStats, setGuardianStats] = useState({ connected: false, emergencies: 0, lastEvent: null, activeRides: 0 });
  const esRef = useRef(null);

  useEffect(() => {
    // Fetch active rides count
    const token = localStorage.getItem('token');
    fetch(`${API}/orchestrator/safety/active`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setGuardianStats(prev => ({ ...prev, activeRides: d.count || 0 })))
      .catch(() => {});

    // SSE for live status
    const es = new EventSource(`${API}/orchestrator/events/stream`);
    esRef.current = es;
    es.onopen = () => setGuardianStats(prev => ({ ...prev, connected: true }));
    es.onerror = () => setGuardianStats(prev => ({ ...prev, connected: false }));
    es.onmessage = (msg) => {
      try {
        const event = JSON.parse(msg.data);
        if (event.type === 'connected') return;
        setGuardianStats(prev => ({
          ...prev,
          connected: true,
          lastEvent: event.event_type || event.type,
          emergencies: prev.emergencies + (event.event_type === 'emergency' ? 1 : 0),
        }));
      } catch {}
    };
    return () => { es.close(); };
  }, []);

  return (
    <div className="overview-content" data-testid="admin-overview-tab">
      {/* Live Guardian Status */}
      <div data-testid="guardian-live-status" style={{
        display: 'flex', alignItems: 'center', gap: 20,
        background: 'linear-gradient(135deg, #0f172a, #1e293b)',
        borderRadius: 12, padding: '14px 20px',
        border: `1px solid ${guardianStats.connected ? '#22c55e30' : '#ef444430'}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Radio size={16} style={{ color: guardianStats.connected ? '#22c55e' : '#ef4444' }} />
          <span style={{ color: '#e2e8f0', fontWeight: 600, fontSize: 14 }}>Guardian</span>
          <span style={{
            width: 7, height: 7, borderRadius: '50%',
            background: guardianStats.connected ? '#22c55e' : '#ef4444',
            display: 'inline-block',
          }} />
        </div>
        <div style={{ display: 'flex', gap: 20, fontSize: 12, color: '#94a3b8' }}>
          <span><Shield size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />{guardianStats.activeRides} Monitored</span>
          <span style={{ color: guardianStats.emergencies > 0 ? '#ef4444' : '#94a3b8' }}>
            <AlertTriangle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />{guardianStats.emergencies} Emergencies
          </span>
          {guardianStats.lastEvent && (
            <span>Last: {guardianStats.lastEvent.replace(/_/g, ' ')}</span>
          )}
        </div>
      </div>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon users"><Users size={32} /></div>
          <div className="stat-info">
            <h3>{stats?.total_users || 0}</h3>
            <p>{t('admin.overview.totalUsers')}</p>
            {stats?.today_users > 0 && <small>+{stats.today_users} {t('admin.overview.today')}</small>}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon rides"><Car size={32} /></div>
          <div className="stat-info">
            <h3>{stats?.total_rides || 0}</h3>
            <p>{t('admin.overview.totalRides')}</p>
            <small>{stats?.active_rides || 0} {t('admin.overview.active')}</small>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon bookings"><Calendar size={32} /></div>
          <div className="stat-info">
            <h3>{stats?.total_bookings || 0}</h3>
            <p>{t('admin.overview.bookings')}</p>
            {stats?.today_bookings > 0 && <small>+{stats.today_bookings} {t('admin.overview.today')}</small>}
          </div>
        </div>
        {revenue && (
          <div className="stat-card highlight">
            <div className="stat-icon revenue"><DollarSign size={32} /></div>
            <div className="stat-info">
              <h3>{revenue?.total_volume || 0}€</h3>
              <p>{t('admin.overview.revenueGross')}</p>
              <small>{revenue?.total_revenue || 0}€ {t('admin.overview.profit')}</small>
            </div>
          </div>
        )}
      </div>

      <div className="admin-actions-panel">
        <h3>{t('admin.overview.systemManagement')}</h3>
        <div className="actions-grid">
          <button className="action-btn primary" onClick={updateAllDriverStats} disabled={updatingStats}>
            <Database size={20} />
            <div>
              <strong>{t('admin.overview.updateDriverStats')}</strong>
              <p>{t('admin.overview.updateDriverStatsDesc')}</p>
            </div>
            {updatingStats ? <div className="spinner-small"></div> : <span>→</span>}
          </button>
          <button className="action-btn secondary" onClick={runMaintenanceTasks} disabled={maintenanceRunning}>
            <Wrench size={20} />
            <div>
              <strong>{t('admin.overview.runMaintenance')}</strong>
              <p>{t('admin.overview.runMaintenanceDesc')}</p>
            </div>
            {maintenanceRunning ? <div className="spinner-small"></div> : <span>→</span>}
          </button>
          <button className="action-btn info" onClick={testEndpoints}>
            <BarChart size={20} />
            <div>
              <strong>{t('admin.overview.runApiTest')}</strong>
              <p>{t('admin.overview.runApiTestDesc')}</p>
            </div>
            <span>→</span>
          </button>
        </div>
      </div>

      {/* ADVANCED STATS */}
      <div className="advanced-stats">
        <div className="stat-box">
          <h4>{t('admin.overview.systemPerformance')}</h4>
          <div className="performance-metrics">
            <div className="metric">
              <span className="metric-label">{t('admin.overview.completionRate')}</span>
              <div className="metric-value">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${stats?.completion_rate || 0}%` }}></div>
                </div>
                <span>{stats?.completion_rate || 0}%</span>
              </div>
            </div>
            <div className="metric">
              <span className="metric-label">{t('admin.overview.activeRides')}</span>
              <span className="metric-value">{stats?.active_rides || 0}</span>
            </div>
            <div className="metric">
              <span className="metric-label">{t('admin.overview.pendingBookings')}</span>
              <span className="metric-value">{stats?.pending_bookings || 0}</span>
            </div>
          </div>
        </div>
        
        <div className="stat-box">
          <h4>{t('admin.overview.financialOverview')}</h4>
          {revenue ? (
            <div className="revenue-details">
              <div className="revenue-item">
                <span>{t('admin.overview.grossVolume')}</span>
                <strong>{revenue.total_volume || 0}€</strong>
              </div>
              <div className="revenue-item">
                <span>{t('admin.overview.pendingPayout')}</span>
                <strong className="pending">{revenue.pending_amount || 0}€</strong>
              </div>
              <div className="revenue-item">
                <span>{t('admin.overview.refunded')}</span>
                <strong className="refunded">{revenue.refunded_amount || 0}€</strong>
              </div>
              <div className="revenue-item total">
                <span>{t('admin.overview.platformProfit')}</span>
                <strong>{revenue.total_revenue || 0}€</strong>
              </div>
            </div>
          ) : (
            <p className="no-data">{t('admin.overview.noFinancialData')}</p>
          )}
        </div>
      </div>
    </div>
  );
};
